import { Button, ClickEvent, ContextMenuItemCreator, ContextMenuLabelProps, Snackbar, Table, TableRow, TableSelectionEvent } from "@mcleod/components";
import { AutogenLayoutStatusListToolPanel } from "./autogen/AutogenLayoutStatusListToolPanel";
import { CommonDialogs } from "@mcleod/common";
import { Api } from "@mcleod/core";
import { StatusList } from "./StatusList";
import { ImageName } from "@mcleod/images/src/ImageName";

export class StatusListToolPanel extends AutogenLayoutStatusListToolPanel {
    table: Table;

    private getAllVisibleButtons(): Button[] {
        return this.panelButtons?.findComponentsByType(Button)?.filter(button => button.visible);
    }

    initialize(table?: Table) {
        this.table = table;
        if (this.getAllVisibleButtons()?.length > 0) {
            this.table?.addSelectionListener((event: TableSelectionEvent) => this.tableStatusesOnSelect(event))
        } else {
            this.removeAll();
        }
    }

    static getContextMenuItems(toolPanel: StatusListToolPanel, tableRow: TableRow): Partial<ContextMenuLabelProps>[] {
        const selectedRows = tableRow?.table?.selectedRows;
        const statusId = selectedRows?.length > 1 && selectedRows?.includes(tableRow)
        ? null : tableRow?.data?.get("id");
        return StatusList.addNavigationContextMenuItems(toolPanel.getContextMenuItems(tableRow), statusId)
    }

    public getContextMenuItems(tableRow: TableRow): Partial<ContextMenuLabelProps>[] {
        const selectedRows = tableRow.table.selectedRows;
        if (!selectedRows.includes(tableRow)) {
            tableRow.table.selectedRow = tableRow;
        }
        const fromButtons = this.getAllVisibleButtons().map(button => ContextMenuItemCreator.fromButton(button));
        return [...fromButtons];
    }

    async onClickManualTransmit(event: ClickEvent) {
        try {
            const selected = this.returnedSelectedRowsForStatus();
            const count = selected.length;
            if (count > 0) {
                const messageGenerated = await this.generateYesNoMessage(selected);
                const message = "Do you want to submit\n\n" + messageGenerated + "\nfor transmission?";
                CommonDialogs.showYesNo(message, "Manual Transmit",
                    { titleProps: { imageName: ImageName.circleX, imageColor: "primary.light" } }).then(async yes => {
                        this.buttonManuallyTransmit.busy = true;
                        if (yes) {
                            await Api.post("lme/datafusion/manual-edi-transmit", { transmit_ids: selected, is_billing: false }).then(result => {
                                //ERROR SNACKBAR
                                const { data = [] } = result || {};
                                const { error: errors = [], error_number: errorCount = 0 } = data[0] || {};
                                
                                if (errorCount > 0) {
                                    const successCount = count - errorCount;
                                    const message = `${successCount} of ${count} batches were submitted for transmission.`;
                                    const errorDetails = Array.isArray(errors) ? errors.join(", ") : errors;
                                    Snackbar.showWarningSnackbar(`${message}\n${errorDetails}`);
                                } else {
                                    Snackbar.showSuccessSnackbar(`The selected batches (${count}) have been submitted for transmission.`);
                                }
                            }).catch(error => {
                                console.error("Error transmitting invoices", error);
                            });
                        }
                    }).finally(() => {
                        this.buttonManuallyTransmit.busy = false;
                    });
            }
        } catch (error) {
            Snackbar.showWarningSnackbar("Error when attempting to manual transmit: " + error.message);
            this.buttonManuallyTransmit.busy = false;
        }
    }

    returnedSelectedRowsForStatus(): any[] {
        const selectedRows = this.table?.selectedRows || [];
        // Use a Map to track unique partner_id and batch pairs
        const uniquePairMap = new Map<string, any>();
        
        // Add each row to the map with partner_id:batch as the key
        selectedRows.forEach((tableRow) => {
            const partnerId = tableRow.data.get("partner_id");
            const batch = tableRow.data.get("batch");
            const readyToSend = tableRow.data.get("ready_to_send");
            if (partnerId  && readyToSend === "Y") {
                const compositeKey = `${partnerId}:${batch}`;
                if (!uniquePairMap.has(compositeKey)) {
                    uniquePairMap.set(compositeKey, {
                        id: tableRow.data.get("id"),
                        customer_id: tableRow.data.get("customer_id"),
                        partner_id: partnerId,
                        partner_name: tableRow.data.get("edistatus_profile.partner_name"),
                        direction: tableRow.data.get("direction"),
                        batch: batch,
                        version: tableRow.data.get("version"),
                        alt_partner_id: tableRow.data.get("alt_partner_id")
                    });
                }
            }
        });
        
        // Return the values from the map
        return Array.from(uniquePairMap.values());
    }

    tableStatusesOnSelect(event: TableSelectionEvent) {
        const selectedRows = this.table?.selectedRows || [];
        const hasSelection = selectedRows.length > 0;

        this.getAllVisibleButtons().forEach(button => {
            button.disabledTooltip = null;
            button.enabled = hasSelection && selectedRows.every(row => row.data != null);
            this.syncButtonState(button, selectedRows);
        });
    }

    private syncButtonState(button: Button, selectedRows: TableRow[]) {
        if (button === this.buttonManuallyTransmit) {
            this.syncButtonManuallyTransmit(selectedRows);
        }
    }

    private syncButtonManuallyTransmit(selectedRows: TableRow[]) {
        const selectedRowsLocal = selectedRows || [];
        const partnerIdSet = new Set<string>();
        let invalidValues = false;
        selectedRowsLocal.forEach((tableRow) => {
            const partnerId = tableRow.data.get("partner_id");
            const readyToSend = tableRow.data.get("ready_to_send");
            const hasError = tableRow.data.get("has_error_out");
            const direction = tableRow.data.get("direction");
            if (partnerId) {
                if (!partnerIdSet.has(partnerId)) {
                    partnerIdSet.add(partnerId);
                }
                if (readyToSend !== "Y" || hasError !== "--" || direction !== "O") {
                    invalidValues = true;
                }
            }
        });
        if (partnerIdSet.size > 1 || invalidValues) {
            this.buttonManuallyTransmit.enabled = false;
            this.buttonManuallyTransmit.disabledTooltip = "Cannot transmit batches with multiple partner IDs or invalid values.";
        } else if (partnerIdSet.size === 1) {
            this.buttonManuallyTransmit.enabled = true;
            this.buttonManuallyTransmit.disabledTooltip = null;
        } else {
            this.buttonManuallyTransmit.enabled = false;
            this.buttonManuallyTransmit.disabledTooltip = "Please select one or more valid records to transmit.";
        }
    }

    async generateYesNoMessage(selected: any[]): Promise<string> {
        const response = await Api.post("lme/datafusion/number-of-transactions", {transmit_ids: selected.sort((a, b) => a.batch - b.batch), is_billing: false});
        return response?.data[0]?.transactions_message;
    }
}